/**
 * Reload attempt bookkeeping for Level 3.
 *
 * A successful reload wipes every module-level variable, so the cooldown
 * cannot live in memory — a reload would reset it right when it matters
 * most. sessionStorage survives the reload but is cleared when the tab
 * actually closes, which matches "per session" exactly.
 *
 * No per-session attempt cap: a hard cap meant a tab left open all day could
 * hit two legitimate long absences (lunch, then an afternoon meeting) and be
 * permanently unrecoverable for the rest of the session — defeating the
 * exact "leave a trading tab open for hours" case this feature exists for.
 * The cooldown stops generic tight reload loops. Backend stream failures get a
 * separate temporary circuit breaker below because reloading cannot repair them.
 */
import { BACKEND_STREAM_RELOAD_SUPPRESSION_MS, RELOAD_COOLDOWN_MS } from './health-policy'
import type { RestartResult, ResyncAckResult } from './types'

const STORAGE_KEY = 'orderx:runtime-recovery:reload-guard'

type ReloadGuardState = {
  /** Kept for telemetry/debugging visibility only — not used to block. */
  count: number
  lastReloadAt: number
  /** Latest confirmed `restart ready + dump ACK timeout` outcome. */
  backendStreamFailureAt: number
}

const EMPTY_STATE: ReloadGuardState = { count: 0, lastReloadAt: 0, backendStreamFailureAt: 0 }

function readState(): ReloadGuardState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return EMPTY_STATE
    const parsed = JSON.parse(raw)
    if (typeof parsed?.count === 'number' && typeof parsed?.lastReloadAt === 'number') {
      return {
        count: parsed.count,
        lastReloadAt: parsed.lastReloadAt,
        // Backward-compatible with sessions written before this field existed.
        backendStreamFailureAt:
          typeof parsed.backendStreamFailureAt === 'number' ? parsed.backendStreamFailureAt : 0,
      }
    }
    return EMPTY_STATE
  } catch {
    // sessionStorage unavailable (private mode) or a corrupt entry — treat as fresh
    // rather than blocking Level 3 forever.
    return EMPTY_STATE
  }
}

function writeState(state: ReloadGuardState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Best-effort. A failed write just means the guard resets on the next load.
  }
}

/** Whether Level 3 may reload right now — cooldown only, see file doc comment. */
export function canAttemptReload(): boolean {
  const { lastReloadAt } = readState()
  if (lastReloadAt !== 0 && Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) return false
  return true
}

/** Call immediately before executing window.location.reload(). */
export function recordReloadAttempt(): void {
  const state = readState()
  writeState({ ...state, count: state.count + 1, lastReloadAt: Date.now() })
}

/** Record strong evidence that reload cannot repair the currently failing dependency. */
export function recordBackendStreamFailure(): void {
  writeState({ ...readState(), backendStreamFailureAt: Date.now() })
}

export function isBackendStreamFailure(
  restartResult: RestartResult | null,
  ackResult: ResyncAckResult | null
): boolean {
  return restartResult === 'ready' && ackResult === 'timeout'
}

/** A complete restart + dump cycle proves the stream path is healthy again. */
export function clearBackendStreamFailure(): void {
  const state = readState()
  if (state.backendStreamFailureAt === 0) return
  writeState({ ...state, backendStreamFailureAt: 0 })
}

export function isBackendStreamReloadSuppressed(): boolean {
  const { backendStreamFailureAt } = readState()
  return (
    backendStreamFailureAt !== 0 &&
    Date.now() - backendStreamFailureAt < BACKEND_STREAM_RELOAD_SUPPRESSION_MS
  )
}

/** Test seam. */
export function resetReloadGuardForTest(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
